import { Check, ChevronDown, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CMS_SYNC_CAPABILITIES,
  hasCmsAutoSync,
} from '@/utils/externalSystemUtils';

interface CmsSyncCapabilitiesProps {
  detectedCms: string | null;
  /** Whether the panel starts open (default: true) */
  defaultOpen?: boolean;
  /** Additional className for the outer container */
  className?: string;
}

function getDetectedRowClass(
  isDetected: boolean,
  hasAutoSync: boolean,
): string {
  if (!isDetected) {
    return '';
  }

  return hasAutoSync
    ? 'bg-emerald-50 dark:bg-emerald-950/20'
    : 'bg-zinc-100 dark:bg-zinc-800/50';
}

/** Collapsible panel showing document syncing status per CMS. */
export function CmsSyncCapabilities({
  detectedCms,
  defaultOpen = true,
  className,
}: CmsSyncCapabilitiesProps): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={`rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700 ${className ?? ''}`}
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between bg-zinc-50 px-3 py-2 text-left text-xs transition-colors hover:bg-zinc-100 dark:bg-zinc-800/50 dark:hover:bg-zinc-800">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-3.5 w-3.5 text-zinc-500" />
          <span className="font-medium text-zinc-700 dark:text-zinc-300">
            Document Syncing
          </span>
          {hasCmsAutoSync(detectedCms) && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Automated
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="divide-y divide-zinc-100 border-t border-zinc-200 dark:divide-zinc-800 dark:border-zinc-700">
          {CMS_SYNC_CAPABILITIES.map((cms) => {
            const isDetected = detectedCms === cms.key;
            const rowClassName = getDetectedRowClass(
              isDetected,
              cms.hasAutoSync,
            );

            return (
              <div
                key={cms.key}
                className={`flex items-center justify-between px-3 py-1.5 text-xs ${rowClassName}`}
              >
                <span
                  className={`font-medium ${isDetected ? 'text-zinc-900 dark:text-white' : 'text-zinc-600 dark:text-zinc-400'}`}
                >
                  {cms.name}
                  {isDetected && (
                    <span className="ml-1.5 text-xs font-normal text-zinc-400">
                      (detected)
                    </span>
                  )}
                </span>
                {cms.hasAutoSync ? (
                  <span className="flex items-center gap-1 font-medium text-emerald-600">
                    <Check className="h-3 w-3" />
                    Automated
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-zinc-400">
                    <X className="h-3 w-3" />
                    Not available
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
